<script setup>
import { computed, ref } from 'vue';
import { useQuery } from 'villus';
import groupBy from 'lodash-es/groupBy';
import { GA } from '@/mixins/Ga';
import { useStore } from '@/store/Store.js';
import BtnPill from '@/components/btn/BtnPill.vue';

const props = defineProps({
    ingredientIds: { type: Object, default: () => {} },
    coverColor: { type: String, default: '' },
    cta: { type: String, default: 'Voir tous les rabais' },
});

const store = useStore();
const ctaText = ref('Voir tous les rabais');
const variables = computed(() => ({ lang: store.lang.value, discountsIngredients: props.ingredientIds }));
const ga = new GA({ category: 'midBody', action: 'clicExterne' });
const sortedDiscounts = ref(null);

const formatSubtitle = (text) => {
    return text.replace(/1er/g, '1<sup>er</sup>');
};

const q = `query FetchEntries($lang: [String], $discountsIngredients: [QueryArgument]) {
    discounts: entries(section: "discount", site: $lang, relatedTo: $discountsIngredients) {
        ... on discount_Entry {
            title,
            prize: discountPrize,
            unit: discountUnit,
            url: discountUrl,
            grocery: discountGrocery {
                ... on superStore_Entry { title, slug },
                ... on saleSpecialist_Entry { title, slug },
                ... on specializedGrocery_Entry { title, slug }
            },
            ingredients: discountIngredients {
                id, title, slug
            }
        }
    },
    discountsMain: entry(section: "discounts", site: $lang) {
        title,
        ... on discounts_Entry {
            subtitle: discountsSubtitle,
            description: discountsDescription,
            legal: discountsLegal,
            cta: discountsCta,
            cover: discountsDiscountsCtaImage { url @transform(format: "webp", quality: 80, width: 500, immediately: true), title }
        }
    }
}`;

const { data } = await useQuery({
    variables,
    query: q,
});
ctaText.value = data?.value?.discountsMain?.cta ? data.value.discountsMain.cta : props.cta;

if (data.value.discounts.length) {
    sortedDiscounts.value = groupBy(data.value.discounts, (el) => el.ingredients[0].title);
}
</script>

<template>
    <div>
        <template v-if="data && sortedDiscounts">
            <div class="DiscountsCta relative bg-light-red py-10 text-white xl:py-12">
                <div class="mx-6 md:mx-12 xl:ml-86 xl:mr-20">
                    <div class="DiscountsCta__head flex flex-col md:flex-row">
                        <div
                            v-if="!portrait"
                            class="DiscountsCta__head-left order-2 mr-6 pb-0 md:order-1 md:-ml-12 md:mr-4 md:pb-4 xl:mx-0 xl:pb-0"
                        >
                            <img
                                v-if="data.discountsMain.cover && data.discountsMain.cover.length > 0"
                                class="DiscountsCta__cover w-140 align-top xl:absolute xl:left-0 xl:top-22 xl:w-85"
                                :src="data.discountsMain.cover[0].url"
                            />
                        </div>
                        <div class="DiscountsCta__head-right order-1 pb-0 md:order-2 md:pb-4 xl:pb-0">
                            <h3 class="h3 DiscountsCta__title em:mb-2">Rabais</h3>

                            <div class="h5 DiscountsCta__description em:mb-4">
                                {{ data.discountsMain.description }}
                            </div>

                            <h4
                                class="h5 DiscountsCta__subtitle em:mb-2"
                                v-html="formatSubtitle(data.discountsMain.subtitle)"
                            ></h4>
                        </div>
                    </div>

                    <div
                        v-for="(discountIngredient, ingredientTitle) in sortedDiscounts"
                        :key="ingredientTitle"
                        class="DiscountsCta__listwrap my-4"
                    >
                        <h4 class="h5 mb-2 text-dark-blue">
                            {{ ingredientTitle }}
                        </h4>
                        <div class="DiscountsCta__list groupnot">
                            <a
                                v-for="discount in discountIngredient"
                                :key="discount.title"
                                class="DiscountsCta__link group block transition duration-500 can-hover:groupnot-hover:opacity-50"
                                :href="discount.url"
                                target="_blank"
                                v-on:click="ga.send({ label: discount.url, camelCase: false })"
                            >
                                <div
                                    class="DiscountsCta__discount flex flex-wrap items-start justify-between border-t-2 border-dark-blue/10 bg-white p-4 text-dark-blue group-last:rounded-b-xl group-first:rounded-t-xl group-first:border-t-0 md:flex-nowrap md:items-center"
                                >
                                    <div
                                        class="DiscountsCta__name shrink-0 grow-0 basis-[60%] pr-8 transition duration-500 group-hover:translate-x-4 md:basis-[49%] xl:pr-10"
                                    >
                                        {{ discount.title }}
                                    </div>
                                    <div class="DiscountsCta__infos basis-[31%] text-right md:text-left">
                                        <h5 class="DiscountsCta__prize inline-block text-2xl font-medium xl:text-3xl">
                                            {{ discount.prize }}<sup class="align-sup ml-1 text-sm">$</sup>
                                            <span
                                                v-if="discount.unit"
                                                class="DiscountsCta__unit block text-sm font-normal md:inline xl:ml-1 xl:align-super"
                                                >/{{ discount.unit }}</span
                                            >
                                        </h5>
                                    </div>
                                    <div class="DiscountsCta__btn mt-4 flex lg:justify-end xl:mt-0 xl:basis-[30%]">
                                        <Btn
                                            class="bg-transparent text-dark-blue"
                                            size="small"
                                            :up="true"
                                            :text="discount.grocery[0].title"
                                        />
                                    </div>
                                </div>
                            </a>
                        </div>
                    </div>

                    <div
                        v-if="ctaText"
                        class="flex"
                    >
                        <BtnPill
                            class="ml-auto mt-2"
                            color="dark-blue"
                            icon="dollar"
                            :to="{ name: 'discounts.' + lang }"
                            v-on:click="ga.send({ label: 'voirTousRabais' })"
                        >
                            <template #text>
                                {{ cta || data.infos.cta }}
                            </template>
                        </BtnPill>
                    </div>

                    <img
                        v-if="portrait"
                        src="https://s3.ca-central-1.amazonaws.com/medias-web.quebecbio.com/uploads/_500xAUTO_crop_center-center_none/LegumesBioRabais-1-1.png"
                        class="DiscountsCta__cover -ml-6 w-80 align-top"
                    />
                </div>
            </div>
        </template>
    </div>
</template>
